import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Archive, ArrowRight, Loader } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { StatCard } from "../../components/shared/StatCard";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

// v1.6 : cycle de vie complet — nouvelle → (lue / en cours) → résolue → archivée.
// « Lue » n'est jamais « résolue ». Le compteur actif = non résolues + non archivées.
const ENTITY_LINKS = {
  reservation: (id) => `/admin/reservations/${id}`,
  contact_message: () => "/admin/messages",
  appointment: () => "/admin/rendez-vous",
  incident: () => "/admin/incidents",
  block: () => "/admin/planning",
  quote: () => "/admin/devis",
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState(null);
  const [tab, setTab] = useState("actives"); // actives | traitees | archivees
  const navigate = useNavigate();

  const load = () => api.get("/admin/notifications").then((r) => setNotifications(r.data)).catch(() => setNotifications([]));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/admin/notifications/${id}/${action}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markAllRead = async () => {
    const unread = (notifications || []).filter((n) => !n.read && !n.resolved && !n.archived);
    await Promise.all(unread.map((n) => api.post(`/admin/notifications/${n.id}/read`).catch(() => {})));
    load();
  };

  if (notifications === null) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="notifications-loading">Chargement…</div>;

  const active = notifications.filter((n) => !n.resolved && !n.archived);
  const treated = notifications.filter((n) => n.resolved && !n.archived);
  const archived = notifications.filter((n) => n.archived);
  const shown = tab === "actives" ? active : tab === "traitees" ? treated : archived;
  const urgent = active.filter((n) => n.priority === "Urgent").length;
  const important = active.filter((n) => n.priority === "Important").length;

  return (
    <div data-testid="admin-notifications-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B24 — Centre de notifications &amp; Alertes</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Centre de notifications &amp; alertes</h1>
          <p className="mt-1 text-sm text-muted-foreground">« Lue » ne signifie pas « traitée » — chaque alerte suit son propre cycle : lue → en cours → résolue → archivée.</p>
        </div>
        <button type="button" onClick={markAllRead} className="btn-secondary-ae !px-5 !py-2.5 !text-xs" data-testid="admin-mark-all-read">
          <CheckCheck className="h-4 w-4" /> Tout marquer comme lu
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <StatCard icon={Bell} label="À traiter" value={active.length} tone={active.length ? "alert" : "default"} />
        <StatCard icon={Bell} label="Urgentes" value={urgent} tone={urgent ? "alert" : "default"} />
        <StatCard icon={Bell} label="Importantes" value={important} tone={important ? "gold" : "default"} />
      </div>

      <div className="mt-6 flex gap-2" role="tablist">
        {[["actives", `Actives (${active.length})`], ["traitees", `Traitées (${treated.length})`], ["archivees", `Archivées (${archived.length})`]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)} data-testid={`notifications-tab-${id}`}
            className={tab === id ? "btn-primary-ae !px-5 !py-2 !text-xs" : "btn-secondary-ae !px-5 !py-2 !text-xs"}>
            {label}
          </button>
        ))}
      </div>

      <div className="card-ae mt-4">
        {shown.length === 0 ? (
          <div className="p-5"><EmptyState icon={Bell} title={tab === "actives" ? "Aucune alerte active" : "Rien ici"}>{tab === "actives" ? "Retours en retard, incidents et conflits de disponibilité apparaîtront ici automatiquement." : "Les alertes conserveront leur historique dans cet onglet."}</EmptyState></div>
        ) : (
          <ul className="divide-y divide-border" data-testid="admin-notifications-list">
            {shown.map((n) => (
              <li key={n.id} className={`flex flex-wrap items-center gap-4 px-5 py-4 transition-colors hover:bg-cream/60 ${!n.read ? "bg-sage-light/50" : ""}`} data-testid={`admin-notification-${n.id}`}>
                <StatusBadge status={n.priority} />
                <button
                  type="button"
                  onClick={() => { const to = ENTITY_LINKS[n.entity]?.(n.entity_id); if (to) navigate(to); }}
                  className="flex-1 text-left"
                  data-testid={`notification-open-${n.id}`}
                >
                  <p className="inline-flex items-center gap-1.5 text-sm text-foreground/85 hover:text-deepgreen">
                    {n.text}
                    {ENTITY_LINKS[n.entity] && <ArrowRight className="h-3.5 w-3.5 text-sage" />}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {(n.created_at || "").slice(0, 16).replace("T", " ")}
                    {!n.read && " — Non lue"}
                    {n.in_progress && !n.resolved && " — En cours de traitement"}
                    {n.resolved && ` — Traitée${n.auto_resolved ? " automatiquement" : ""}`}
                    {n.archived && " — Archivée"}
                  </p>
                </button>
                <div className="flex flex-wrap gap-2">
                  {!n.read && !n.archived && <button type="button" onClick={() => act(n.id, "read")} className="rounded-full border border-border px-3.5 py-1.5 text-[11px] font-bold text-deepgreen transition-colors hover:bg-sage-light" data-testid={`notification-read-${n.id}`}>Marquer lue</button>}
                  {!n.in_progress && !n.resolved && !n.archived && <button type="button" onClick={() => act(n.id, "in-progress")} className="rounded-full border border-border px-3.5 py-1.5 text-[11px] font-bold text-deepgreen transition-colors hover:bg-sage-light" data-testid={`notification-progress-${n.id}`}><Loader className="inline h-3 w-3" /> En cours</button>}
                  {!n.resolved && !n.archived && <button type="button" onClick={() => act(n.id, "resolve")} className="rounded-full border border-border px-3.5 py-1.5 text-[11px] font-bold text-deepgreen transition-colors hover:bg-sage-light" data-testid={`notification-resolve-${n.id}`}>Marquer traitée</button>}
                  {!n.archived && <button type="button" onClick={() => act(n.id, "archive")} className="rounded-full border border-border px-3.5 py-1.5 text-[11px] font-bold text-muted-foreground transition-colors hover:bg-cream" data-testid={`notification-archive-${n.id}`}><Archive className="inline h-3 w-3" /> Archiver</button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
